// @flow strict

import * as React from 'react'
import cx from 'classnames'
import idx from 'idx'

import { useEffect, useRef, useState } from 'react'
import { type NavStyles } from './NavigationComponents'
// Styling
import navigationDropdownStyles from './NavigationDropdown.module.scss'

export type NavigationLink = {
  href?: string,
  text: string,
  label?: string,
  openInNewTab?: boolean,
}

export type DropdownItemProps = NavigationLink & {
  icon?: string,
  isNew?: boolean,
}
type DropdownProps = {
  items: DropdownItemProps[],
  isDropdownVisible: boolean,
  currentPath: string,

  setIsDropdownVisible: (visible: boolean) => void,
}

export type NavigationItemProps = NavigationLink & {
  dropdownItems?: Array<DropdownItemProps>,
}

export const NavigationItem = ({
  text,
  href,
  label,
  dropdownItems,
  navigationListStyles,
  openInNewTab = false,
}: NavigationItemProps & { navigationListStyles: NavStyles }) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const currentPath =
    typeof window !== 'undefined'
      ? idx(window, _ => _.location.pathname) || ''
      : ''
  const isCurrentlySelectedItem = href === currentPath.replace(/\/+$/, '') // remove any trailing slashes

  if (dropdownItems) {
    return (
      <li
        className={navigationListStyles.item}
        onMouseLeave={() => setIsDropdownVisible(false)}
      >
        <button
          className={navigationListStyles.toggle}
          onClick={() => setIsDropdownVisible(!isDropdownVisible)}
          data-testid="DropdownToggle"
        >
          {text}
          {label && <span className={navigationListStyles.label}>{label}</span>}
        </button>
        <Dropdown
          isDropdownVisible={isDropdownVisible}
          items={dropdownItems}
          currentPath={currentPath}
          setIsDropdownVisible={setIsDropdownVisible}
        />
      </li>
    )
  }

  return (
    <li className={navigationListStyles.item}>
      <a
        href={href}
        className={cx(navigationListStyles.link, {
          [navigationListStyles.current]: isCurrentlySelectedItem,
        })}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {text}{' '}
        {label && <span className={navigationListStyles.label}>{label}</span>}
      </a>
    </li>
  )
}

const Dropdown = ({
  items,
  isDropdownVisible,
  setIsDropdownVisible,
  currentPath,
}: DropdownProps) => {
  const dropdown: {
    current: null | HTMLUListElement,
  } = useRef(null)
  // This allows you to focus into the dropdown and keep the dropdown open
  // And it closes the dropdown, when you focus out of it
  useEffect(() => {
    const currentDropdown = dropdown.current
    if (!currentDropdown) return

    const toggleDropdownVisibility = (
      evt: Event & { relatedTarget?: HTMLElement },
    ) => setIsDropdownVisible(currentDropdown.contains(evt.relatedTarget))
    currentDropdown.addEventListener('focusout', toggleDropdownVisibility)

    return () => {
      currentDropdown.removeEventListener('focusout', toggleDropdownVisibility)
    }
  }, [dropdown, setIsDropdownVisible])
  const listClasses = isDropdownVisible
    ? navigationDropdownStyles.dropdownVisible
    : navigationDropdownStyles.dropdownHidden

  return (
    <ul className={listClasses} ref={dropdown} data-testid="Dropdown">
      {items.map(
        ({ href, text, icon, openInNewTab = false, isNew = false }) => {
          const linkClasses = icon
            ? navigationDropdownStyles.linkWithIcon
            : navigationDropdownStyles.link
          return (
            <li key={text}>
              <a
                href={href}
                className={cx(linkClasses, {
                  [navigationDropdownStyles.current]: currentPath === href,
                })}
                data-testid={icon ? 'DropdownLinkFooter' : 'DropdownLink'}
                target={openInNewTab ? '_blank' : undefined}
                rel={openInNewTab ? 'noopener noreferrer' : undefined}
              >
                {icon && (
                  <img
                    src={icon}
                    alt=""
                    className={navigationDropdownStyles.icon}
                  />
                )}
                {text}
                {isNew && (
                  <span
                    className={navigationDropdownStyles.newBadge}
                    aria-hidden
                  />
                )}
              </a>
            </li>
          )
        },
      )}
    </ul>
  )
}
